import styled from 'styled-components';
import Icon from '@mdi/react';
import { Hero } from '../sections/Hero';
import { ServicesGrid } from '../sections/ServicesGrid';
import { CTASection } from '../sections/CTASection';
import { Section, SectionHeader } from '../ui/Section';
import { GlassCard } from '../ui/GlassCard';
import { processSteps } from '../../data/services';
import { PageHead } from '../seo';

const PageWrapper = styled.div`
  padding-top: 73px;
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--space-6);

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ProcessCard = styled(GlassCard)`
  text-align: center;
  position: relative;
`;

const StepNumber = styled.div`
  width: 40px;
  height: 40px;
  border-radius: var(--radius-full);
  background: linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-secondary));
  color: white;
  font-weight: var(--font-bold);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--space-4);
`;

const IconWrapper = styled.div`
  width: 56px;
  height: 56px;
  border-radius: var(--radius-lg);
  background: var(--color-border);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--space-4);
  color: var(--color-brand-primary);
`;

const StepTitle = styled.h3`
  font-size: var(--text-lg);
  margin-bottom: var(--space-3);
`;

const StepDescription = styled.p`
  font-size: var(--text-sm);
  line-height: var(--leading-relaxed);
`;

export function Services() {
  return (
    <PageWrapper>
      <PageHead
        title="Services"
        description="Web development, mobile apps, e-commerce solutions, custom software, and consulting services. Modern tooling, clean code, on-time delivery."
      />
      <Hero
        eyebrow="Services"
        title={
          <>
            Expertise That{' '}
            <span className="gradient">Delivers</span>
          </>
        }
        subtitle="Whether you need a complete web application, mobile app, e-commerce solution, or workflow automation, we bring the technical skills and creative vision to make it happen."
        primaryCTA={{ label: 'Start a Project', to: '/contact' }}
        secondaryCTA={{ label: 'View Our Work', to: '/portfolio' }}
        showStats={false}
      />

      <ServicesGrid showHeader={false} showViewAll={false} />

      <Section>
        <SectionHeader
          title="Our Process"
          subtitle="A proven approach to delivering successful projects on time and within budget."
          centered
        />

        <ProcessGrid>
          {processSteps.map(step => (
            <ProcessCard key={step.number}>
              <StepNumber>{step.number}</StepNumber>
              <IconWrapper>
                <Icon path={step.icon} size={1.2} />
              </IconWrapper>
              <StepTitle>{step.title}</StepTitle>
              <StepDescription>{step.description}</StepDescription>
            </ProcessCard>
          ))}
        </ProcessGrid>
      </Section>

      <CTASection
        title="Ready to Get Started?"
        subtitle="Let's discuss your project and find the perfect solution for your needs."
        primaryCTA={{ label: 'Contact Us', to: '/contact' }}
        secondaryCTA={{ label: 'View Our Apps', to: '/products' }}
      />
    </PageWrapper>
  );
}

export default Services;
